import { Box, Button, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  useResumeAfterVenueMaintenance,
  useVenueMaintenanceState
} from '../../api/useEntry';

const MAINTENANCE_TRIGGER = 'VENUE_MAINTENANCE';
/** The backend's answer when it could not READ the key — not someone's halt. */
const UNREADABLE = 'unknown';

/**
 * The operator's view of a venue maintenance halt, and the only button that
 * lifts it.
 *
 * The watcher halts BOTH engines ahead of an announced Kalshi/Polymarket
 * maintenance window and — deliberately — never resumes trading on its own. So
 * there has to be a control here: the only other thing that clears the halt key
 * is global START OPENER, which the UI greys out whenever global state already
 * reads "Running". That is the 2026-08-06 deadlock, and a scheduled window
 * would walk into it every single time.
 *
 * EVERYTHING it renders is derived from the polled backend state — never from
 * the resume mutation's own result. This component is mounted on two SEPARATE
 * routes (the opener page and the unwinder page), and the backend drops its
 * held window on every resume, so a verdict cached in the browser disappears
 * the moment the operator navigates: they would resume from the opener page,
 * see "still armed", switch to the unwinder page to check the other direction,
 * and find nothing at all — a false all-clear on the page for the direction
 * most likely still stopped. Reading the halt keys through also means the
 * warning clears itself when someone lifts the other halt, instead of sticking
 * until the next maintenance window.
 */
export const VenueMaintenanceControl = () => {
  const { data } = useVenueMaintenanceState();
  const resume = useResumeAfterVenueMaintenance();

  const held = data?.held ?? null;
  const halts = data?.halts;
  const armFailures = data?.haltArmFailures ?? [];

  const armed = (
    [
      ['opener', halts?.opener],
      ['unwinder', halts?.unwinder]
    ] as const
  ).filter(([, trigger]) => Boolean(trigger));

  // A halt standing on a key that is NOT ours: an operator PANIC or STOP took
  // it over, so pressing resume here will not (and must not) lift it.
  // `unknown` is excluded on purpose — it means the backend could not READ the
  // key, and telling the operator to go clear a stop that does not exist is its
  // own kind of wrong answer.
  const foreign = armed.filter(
    ([, trigger]) => trigger !== MAINTENANCE_TRIGGER && trigger !== UNREADABLE
  );
  const unreadable = armed.filter(([, trigger]) => trigger === UNREADABLE);
  // OUR trigger still armed while the watcher has no window in memory — a halt
  // that survived a restart whose adoption failed, or one orphaned by a race.
  // Nobody is refreshing it and nobody else will clear it, so this is the one
  // state where rendering nothing is the most dangerous thing the control can
  // do: both engines are stopped and the only button that lifts them would be
  // hidden. Show it, and offer the resume. An UNREADABLE key gets the same
  // treatment: it may well be ours, and the resume is harmless if it is not.
  // Per DIRECTION, not globally: the two keys live on different cluster slots,
  // so one read can fail while the other answers. A mixed read must still name
  // the direction we KNOW is halted with no window tracked.
  const orphanedDirections = held
    ? []
    : armed.filter(([, t]) => t === MAINTENANCE_TRIGGER);
  const orphaned =
    !held &&
    armed.some(([, t]) => t === MAINTENANCE_TRIGGER || t === UNREADABLE);

  // `isError` is part of the gate: a resume that never reached the manager has
  // to be visible even when the poll says everything is clear, or a failed
  // request looks exactly like a successful one.
  if (
    !held &&
    !orphaned &&
    foreign.length === 0 &&
    unreadable.length === 0 &&
    armFailures.length === 0 &&
    !resume.isError
  ) {
    return null;
  }

  const waiting = Boolean(data?.awaitingOperator);
  // Only meaningful when the VENUE gave us the end. For an unannounced outage
  // the backend invents a placeholder it explicitly refuses to hold to, and
  // printing it would be a fabricated schedule.
  const scheduleKnown = Boolean(held?.endsAtKnown);
  const windowEnded = Boolean(
    held && scheduleKnown && Date.now() >= held.endsAt
  );
  const minutesLeft =
    held && scheduleKnown
      ? Math.max(0, Math.round((held.endsAt - Date.now()) / 60_000))
      : 0;

  const chipLabel = () => {
    if (!held) return '';
    if (waiting) return `${held.venue} maintenance over — halted`;
    if (!scheduleKnown) return `${held.venue} outage — halted, no end announced`;
    if (windowEnded) return `${held.venue} maintenance — halted, venue unconfirmed`;
    return `${held.venue} maintenance — halted${minutesLeft > 0 ? ` ~${minutesLeft}min` : ''}`;
  };

  const chipTitle = () => {
    if (!held) return '';
    if (waiting) {
      return `${held.venue} maintenance ("${held.title}") is OVER and the venue reports healthy — the engines are still halted until you resume. Nothing restarts on its own.`;
    }
    if (!scheduleKnown) {
      return `${held.venue} is refusing orders and published no end time, so there is no schedule to show. The engines stay halted until the venue reports healthy again.`;
    }
    if (windowEnded) {
      return `${held.venue} maintenance ("${held.title}") is past its scheduled end, but the venue has not confirmed it is healthy again. Resuming now is your call.`;
    }
    return `${held.venue} maintenance in progress ("${held.title}") until ${new Date(held.endsAt).toLocaleTimeString()}.`;
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {held && (
          <Tooltip title={chipTitle()} placement="top">
            <Chip
              size="small"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              color={waiting ? 'warning' : 'error'}
              variant={waiting ? 'outlined' : 'filled'}
              label={chipLabel()}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {unreadable.map(([direction]) => (
          <Tooltip
            key={`unreadable-${direction}`}
            title={`The ${direction} halt key could not be READ, so its state is UNKNOWN — assume the ${direction} is stopped until this clears. It is not evidence that anyone halted it.`}
            placement="top"
          >
            <Chip
              size="small"
              color="warning"
              label={`${direction} halt state UNREADABLE`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {orphanedDirections.map(([direction]) => (
          <Tooltip
            key={`orphan-${direction}`}
            title={`A maintenance halt is still armed on the ${direction} but the watcher has no window for it — most likely it outlived a restart. That engine is STOPPED. Resume lifts it.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              label={`${direction} halt armed — no window tracked`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {(held || orphaned) && (
          // Offered even mid-window: the operator overrides the watcher, not the
          // other way round. The tooltip says plainly what they are overriding.
          <Tooltip
            title={
              orphaned
                ? 'Lift the orphaned maintenance halt so both engines can trade again.'
                : waiting
                  ? 'Lift the maintenance halt and let both engines trade again.'
                  : 'The venue has NOT confirmed it is trading again — resuming now can put the makers back against a venue still refusing orders.'
            }
            placement="top"
          >
            <span>
              <Button
                variant={waiting || orphaned ? 'contained' : 'outlined'}
                color={waiting || orphaned ? 'success' : 'warning'}
                size="small"
                startIcon={
                  resume.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => resume.mutate()}
                disabled={resume.isLoading}
                sx={{ fontSize: '0.7rem' }}
              >
                {waiting || orphaned ? 'Resume trading' : 'Resume anyway'}
              </Button>
            </span>
          </Tooltip>
        )}
        {/* A halt on a key that is not ours. Named by its actual trigger and by
            direction: "PANIC" sends the operator to the panic lockout, "STOP"
            does not, and one direction being held while the other trades is a
            different situation from both being down. */}
        {foreign.map(([direction, trigger]) => (
          <Tooltip
            key={direction}
            title={`The ${direction} halt key is held by ${trigger}, not by the maintenance watcher — resuming here will NOT lift it. Clear that ${trigger === 'PANIC' ? 'panic' : 'stop'} to let the ${direction} trade again.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              label={`${direction} halted by ${trigger}`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {/* The loudest thing this control can say: a direction the watcher could
            NOT halt may still be placing INTO the window. */}
        {armFailures.length > 0 && (
          <Tooltip
            title={`The halt could not be armed on: ${armFailures.join('; ')}. That engine may still be placing into the maintenance window — check it by hand.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="halt NOT armed on one direction"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {resume.isError && (
          <Tooltip
            // Deliberately does NOT claim the halt is still armed: the resume
            // clears the keys BEFORE the steps that can throw, so a 500 here is
            // consistent with the engines being live. The chips above say what
            // is actually armed; this one only says the request failed.
            title="The resume request failed. Check the halt chips above for what is actually armed before assuming anything changed."
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="resume request FAILED"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
